/**
 * UnderstandingPromptCard — Blueprint tranche AD-5 (Feb 10, 2026).
 *
 * ONE understanding-oriented prompt rendered on the WorkMode FEEDBACK
 * stage AFTER the AD-4 Adaptive Reflection card resolves. Silent by
 * default — the card only renders when the AD-5 orchestration endpoint
 * returns a selected question backed by genuine evidence AND AD-4 was
 * not dismissed. The instructor-visible progression is:
 *   Experience → Notice → Reflect → Understand → Plan
 *   (Q6)         (AD-3)   (AD-4)    (AD-5)       (later)
 *
 * Operator brief (locked):
 *   • Q6 remains verbatim on ENDING. AD-3/AD-4 flows unchanged.
 *   • Maximum one optional understanding prompt per lesson.
 *   • Genuine voice-first: primary CTA begins voice recording. The
 *     textarea does NOT render or receive focus until the instructor
 *     deliberately opens the typing fallback.
 *   • No autoFocus anywhere.
 *   • Skip / Close create no debt, no score change, no negative
 *     evidence, no coaching recommendation.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Mic, MicOff, Check } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * @param {object} props
 * @param {object} props.lesson       — lesson doc (id required).
 * @param {boolean} props.ad4Resolved — flips true once AD-4 has
 *   resolved (answered / skipped / swapped / closed / silent).
 * @param {function} [props.onResolved]
 */
export default function UnderstandingPromptCard({ lesson, ad4Resolved, onResolved }) {
  const [selection, setSelection] = useState(null);
  const [phase, setPhase] = useState("prompt");      // prompt | recording | typing | done
  const [captured, setCaptured] = useState("");       // transcript / typed content
  const [busy, setBusy] = useState(false);
  const finalisedRef = useRef(false);
  const presentedRef = useRef(false);
  const recognitionRef = useRef(null);
  const typedFieldRef = useRef(null);

  // Feature-detect Web Speech recognition (voice-first primary path).
  const SpeechRecognitionCtor =
    typeof window !== "undefined"
      ? (window.SpeechRecognition || window.webkitSpeechRecognition)
      : null;
  const voiceSupported = !!SpeechRecognitionCtor;

  // Fetch the selection ONLY after AD-4 resolved.
  useEffect(() => {
    if (!lesson?.id || !ad4Resolved) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/api/work-mode/lessons/${lesson.id}/understanding-prompt`,
          { withCredentials: true },
        );
        if (cancelled) return;
        if (data?.selected_question_key) {
          setSelection(data);
        }
      } catch {
        // Evidence-driven silence is a legal outcome. Never surface
        // this to the instructor.
      }
    })();
    return () => { cancelled = true; };
  }, [lesson?.id, ad4Resolved]);

  // Record a presentation ONCE after real render.
  useEffect(() => {
    if (!selection?.selected_question_key || presentedRef.current) return;
    presentedRef.current = true;
    (async () => {
      try {
        await axios.post(
          `${API}/api/work-mode/lessons/${lesson.id}/understanding-prompt/presented`,
          { selected_question_key: selection.selected_question_key },
          { withCredentials: true },
        );
      } catch {
        // Ledger blip must not block the instructor experience.
      }
    })();
  }, [selection, lesson?.id]);

  // Deliberate focus on typed reveal only. Never on mount.
  useEffect(() => {
    if (phase === "typing" && typedFieldRef.current) {
      typedFieldRef.current.focus();
    }
  }, [phase]);

  // Genuine closed_without_action on unmount, only if we truly
  // rendered and the instructor never chose a terminal route.
  useEffect(() => {
    return () => {
      if (
        !selection?.selected_question_key ||
        finalisedRef.current ||
        !presentedRef.current
      ) return;
      axios.post(
        `${API}/api/work-mode/lessons/${lesson.id}/understanding-prompt/finalize`,
        {
          presented_question_key: selection.selected_question_key,
          final_route: "closed_without_action",
        },
        { withCredentials: true },
      ).catch(() => { /* silent */ });
    };
  }, []);

  if (!lesson?.id || !selection?.selected_question_key || phase === "done") return null;

  // ── Voice recording controls ──────────────────────────────
  const startRecording = () => {
    if (!voiceSupported) {
      // Graceful fallback — reveal the typed pathway directly.
      setPhase("typing");
      return;
    }
    try {
      const recognition = new SpeechRecognitionCtor();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = "en-GB";
      let finalText = captured;
      recognition.onresult = (event) => {
        let interim = "";
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const chunk = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalText = (finalText ? finalText + " " : "") + chunk.trim();
          } else {
            interim += chunk;
          }
        }
        setCaptured(interim ? `${finalText} ${interim}` : finalText);
      };
      recognition.onerror = () => {
        setPhase("prompt");
      };
      recognition.onend = () => {
        setPhase((p) => (p === "recording" ? "prompt" : p));
      };
      recognition.start();
      recognitionRef.current = recognition;
      setPhase("recording");
    } catch {
      setPhase("typing");
    }
  };

  const stopRecording = () => {
    try { recognitionRef.current?.stop(); } catch { /* ignore */ }
    recognitionRef.current = null;
    setPhase("prompt");
  };

  const finalize = async (route) => {
    if (busy || finalisedRef.current) return;
    setBusy(true);
    finalisedRef.current = true;
    try {
      // If we were recording, stop it cleanly first.
      if (recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch { /* ignore */ }
        recognitionRef.current = null;
      }
      await axios.post(
        `${API}/api/work-mode/lessons/${lesson.id}/understanding-prompt/finalize`,
        {
          presented_question_key: selection.selected_question_key,
          final_route: route,
        },
        { withCredentials: true },
      );
      setPhase("done");
      if (typeof onResolved === "function") onResolved(route);
    } catch (err) {
      finalisedRef.current = false;
      const detail = err?.response?.data?.detail || "Couldn't save right now — try again.";
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      data-testid="ad5-understanding-card"
      className="bg-slate-800 rounded-2xl border border-slate-700 p-4 space-y-3"
    >
      <header className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-xl bg-purple-500/15 flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-4 h-4 text-purple-300" />
        </div>
        <div className="flex-1">
          <p className="text-[11px] font-bold uppercase tracking-widest text-purple-300 mb-1">
            Understanding
          </p>
          <p
            data-testid="ad5-understanding-wording"
            className="text-sm font-bold text-white leading-snug"
          >
            {selection.wording}
          </p>
          <p className="text-[11px] text-slate-400 mt-1">
            Before planning the next step — a moment to understand what happened.
          </p>
        </div>
      </header>

      {/* Captured transcript / typed content (read-only preview
          while in prompt phase). Silent when empty. */}
      {phase === "prompt" && captured.trim() && (
        <div
          data-testid="ad5-understanding-captured"
          className="bg-slate-900/40 border border-slate-700 rounded-xl px-3 py-2"
        >
          <p className="text-[10px] uppercase tracking-widest text-slate-500 mb-0.5">
            Captured
          </p>
          <p className="text-sm text-slate-200 leading-relaxed whitespace-pre-wrap">
            {captured}
          </p>
        </div>
      )}

      {phase === "prompt" && (
        <div className="space-y-2">
          {/* PRIMARY · voice-first record button. */}
          <button
            type="button"
            data-testid="ad5-understanding-record-btn"
            onClick={startRecording}
            disabled={busy}
            className="w-full h-12 rounded-xl bg-purple-600 hover:bg-purple-500 text-white font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-60"
          >
            <Mic className="w-4 h-4" />
            {captured.trim() ? "Tap to record more" : "Tap to record"}
          </button>

          {/* SECONDARY row: type instead / skip. */}
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              data-testid="ad5-understanding-type-btn"
              onClick={() => setPhase("typing")}
              disabled={busy}
              className="h-10 rounded-lg bg-slate-700 border border-slate-600 text-slate-100 font-medium text-xs hover:border-slate-500 disabled:opacity-60"
            >
              Prefer to type
            </button>
            <button
              type="button"
              data-testid="ad5-understanding-skip-btn"
              onClick={() => finalize("skipped")}
              disabled={busy}
              className="h-10 rounded-lg bg-transparent border border-slate-700 text-slate-400 font-medium text-xs hover:text-slate-200 disabled:opacity-60"
            >
              Not now
            </button>
          </div>

          {/* DONE button only appears once there is genuine
              captured content. Never coerces an answer. */}
          {captured.trim() && (
            <button
              type="button"
              data-testid="ad5-understanding-done-btn"
              onClick={() => finalize("answered_understanding")}
              disabled={busy}
              className="w-full h-10 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-bold flex items-center justify-center gap-1 disabled:opacity-60"
            >
              <Check className="w-4 h-4" /> Done
            </button>
          )}
        </div>
      )}

      {phase === "recording" && (
        <div className="space-y-2" data-testid="ad5-understanding-recording">
          <button
            type="button"
            data-testid="ad5-understanding-stop-btn"
            onClick={stopRecording}
            className="w-full h-12 rounded-xl bg-red-600/20 border-2 border-red-500/60 text-red-200 font-bold text-sm flex items-center justify-center gap-2"
          >
            <MicOff className="w-4 h-4" />
            <span>Tap to stop</span>
            <span className="w-2 h-2 bg-red-400 rounded-full animate-pulse" />
          </button>
          {captured.trim() && (
            <div className="bg-slate-900/40 border border-slate-700 rounded-xl px-3 py-2">
              <p className="text-sm text-slate-200 italic leading-relaxed whitespace-pre-wrap">
                {captured}
              </p>
            </div>
          )}
        </div>
      )}

      {phase === "typing" && (
        <div className="space-y-2" data-testid="ad5-understanding-typing">
          {/* showTypedFallback pattern (Q6-consistent). Textarea
              only exists in the DOM when the instructor deliberately
              taps "Prefer to type". Focus applied by the effect
              above — never autoFocus. */}
          <div className="flex items-center justify-between">
            <label
              htmlFor="ad5-answer-field"
              className="text-[11px] font-bold uppercase tracking-widest text-slate-400"
            >
              Type your understanding
            </label>
            <button
              type="button"
              data-testid="ad5-understanding-back-to-voice"
              onClick={() => setPhase("prompt")}
              disabled={busy}
              className="text-[11px] text-slate-500 hover:text-slate-300 font-medium underline underline-offset-4 disabled:opacity-60"
            >
              Back to voice
            </button>
          </div>
          <textarea
            id="ad5-answer-field"
            ref={typedFieldRef}
            data-testid="ad5-understanding-answer-field"
            value={captured}
            onChange={(e) => setCaptured(e.target.value)}
            rows={3}
            placeholder="Type what you now understand about today — one calm sentence is enough."
            className="w-full bg-slate-900/60 border border-slate-700 rounded-xl px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-purple-500"
          />
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              data-testid="ad5-understanding-typing-skip-btn"
              onClick={() => finalize("skipped")}
              disabled={busy}
              className="h-10 rounded-lg bg-transparent border border-slate-700 text-slate-400 font-medium text-xs hover:text-slate-200 disabled:opacity-60"
            >
              Not now
            </button>
            <button
              type="button"
              data-testid="ad5-understanding-typing-done-btn"
              onClick={() => finalize("answered_understanding")}
              disabled={busy}
              className="h-10 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-bold flex items-center justify-center gap-1 disabled:opacity-60"
            >
              {busy ? "Saving…" : (<><Check className="w-4 h-4" /> Done</>)}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
